"use client";
import { useTranslation } from "react-i18next";
import { TransitionWrapper } from "./TransitionWrapper";
import { AnimatedDots } from "./AnimateDots";
import { AnimatedGradientText } from "./AnimateGradientText";
import Image from "next/image";
import Link from "next/link";
import { Button } from "@packages/ui/src/components/shadcn/main-button";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface HeroSectionProps {
  className?: string;
}

const HeroSection = ({ className = "" }: HeroSectionProps) => {
  const { t } = useTranslation("main");

  return (
    <section className={`text-center p-6 max-w-3xl w-full ${className}`}>
      <AnimatedDots />
      <div className="ContainerGlob content-center z-30 mx-auto">
        <div className="flex flex-col items-center">
          {/* Logo */}
          <TransitionWrapper
            delay={0.2}
            className="relative mb-8 h-32 w-32 md:h-40 md:w-40"
          >
            <Image
              src="/loghi/BeehiveLogo.png"
              alt={t("hero.logoAlt")}
              fill
              sizes="(max-width: 640px) 128px, (max-width: 1024px) 160px, 160px"
              className="object-contain"
              priority
            />
          </TransitionWrapper>

          {/* Titoli */}
          <div className="font-manrope leading-tight">
            <TransitionWrapper delay={0.3}>
              <h1 className="text-[#000541] text-4xl font-extrabold tracking-tight md:text-5xl lg:text-[70px]">
                {t("hero.welcome")}{" "}
                <AnimatedGradientText>Beehive</AnimatedGradientText>
              </h1>
            </TransitionWrapper>
            <TransitionWrapper delay={0.4}>
              <h2 className="text-[#000541] text-4xl font-extrabold tracking-tight md:text-5xl lg:text-[70px]">
                {t("hero.subtitle1")}
              </h2>
              <h2 className="text-[#000541] text-4xl font-extrabold tracking-tight md:text-5xl lg:text-[70px]">
                {t("hero.subtitle2")}
              </h2>
            </TransitionWrapper>
          </div>

          {/* Bottoni */}
          <TransitionWrapper delay={0.4}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:justify-center sm:gap-6">
              <Button
                className="bg-[#000541] text-white hover:bg-[#000541]/90 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 font-semibold"
                effect="shineHover"
                icon={() => <FontAwesomeIcon icon={faArrowRight} />}
                iconPlacement="right"
                size="lg"
                variant="default"
                asChild
              >
                <Link href="/coworking" className="flex items-center gap-2">
                  {t("hero.buttonCoworking")}
                </Link>
              </Button>

              <Button
                className="border-2 border-[#000541] text-[#000541] bg-white/80 backdrop-blur-sm hover:bg-[#000541] hover:text-white shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-semibold"
                effect="ringHover"
                icon={() => <FontAwesomeIcon icon={faArrowRight} />}
                iconPlacement="right"
                size="lg"
                variant="outline"
                asChild
              >
                <Link href="/digital" className="flex items-center gap-2">
                  {t("hero.buttonDigital")}
                </Link>
              </Button>
            </div>
          </TransitionWrapper>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
